import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { websiteLeadKindSchema, websiteLeadStatusSchema } from '@aimmo/contracts';
import { createResourceRoutes, listSearchSchema, type ListState } from '@aimmo/ui';
import { sessionQuery } from './lib/session.js';
import { useResourcePermissions } from './lib/abilities.js';
import { AppShell } from './routes/app-shell.js';
import { DashboardPage } from './routes/dashboard.js';
import { AgendaPage } from './routes/agenda.js';
import { AdministrationPage } from './routes/administration.js';
import { AgencySettingsPage } from './routes/agency-settings.js';
import { CommunicationsSettingsPage } from './routes/communications-settings.js';
import { appointmentTypesResource } from './resources/appointment-types.js';
import { segmentsResource } from './resources/segments.js';
import { messageTemplatesResource } from './resources/message-templates.js';
import { propertyTypologiesResource } from './resources/property-typologies.js';
import { zonesResource } from './resources/zones.js';
import { ZoneImportDialog } from './features/zone-import-dialog.js';
import { contactsResource } from './resources/contacts.js';
import { buildingsResource } from './resources/buildings.js';
import { demandsResource } from './resources/demands.js';
import { mandatesResource } from './resources/mandates.js';
import { propertiesResource } from './resources/properties.js';

export interface RouterContext {
  queryClient: QueryClient;
}

/*
 * Screens loaded on first visit rather than with the app (W2).
 *
 * The assistant brings a chart library and a markdown renderer, the platform
 * tree is a whole second product that an agency never opens, and the inbox and
 * campaigns are screens most of a working day does not touch. Bundled eagerly
 * they were most of a 1.7 MB entry chunk that every login downloaded.
 */
const WhatsAppPage = lazyRouteComponent(() => import('./routes/whatsapp.js'), 'WhatsAppPage');
const AssistantPage = lazyRouteComponent(() => import('./routes/assistant.js'), 'AssistantPage');
const AssistantUsagePage = lazyRouteComponent(
  () => import('./routes/assistant-usage.js'),
  'AssistantUsagePage',
);
const CampaignsPage = lazyRouteComponent(() => import('./routes/campaigns.js'), 'CampaignsPage');
const CampaignDetailPage = lazyRouteComponent(
  () => import('./routes/campaign-detail.js'),
  'CampaignDetailRoute',
);
const PlatformShell = lazyRouteComponent(
  () => import('./routes/platform/platform-shell.js'),
  'PlatformShell',
);
const PlatformAgenciesPage = lazyRouteComponent(
  () => import('./routes/platform/agencies.js'),
  'PlatformAgenciesPage',
);
const PlatformAgencyDetailPage = lazyRouteComponent(
  () => import('./routes/platform/agency-detail.js'),
  'PlatformAgencyDetailPage',
);
const PlatformUsersPage = lazyRouteComponent(
  () => import('./routes/platform/platform-users.js'),
  'PlatformUsersPage',
);
const AssistantAdminPage = lazyRouteComponent(
  () => import('./routes/platform/assistant-admin.js'),
  'AssistantAdminPage',
);
const PlatformWebsiteLeadsPage = lazyRouteComponent(
  () => import('./routes/platform/website-leads.js'),
  'PlatformWebsiteLeadsPage',
);

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
});

/**
 * Everything below this route requires a session. The loader resolves it once
 * and the shell reads it from the cache — no screen renders before the user is
 * known.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: async ({ context }) => {
    let session;
    try {
      session = await context.queryClient.ensureQueryData(sessionQuery);
    } catch {
      throw redirect({ href: '/api/auth/login' });
    }
    // The product operator has no agency, so none of these screens has anything
    // to show them — and the API refuses every one of them anyway (M12).
    if (session.platform) throw redirect({ to: '/platform' });
    return { session };
  },
  component: AppShell,
});

/**
 * The platform tree (M12), and the mirror image of the guard above: everyone
 * who is not the operator is sent back. The two redirects are what keep a
 * session from ever rendering a shell that assumes the other kind.
 */
const platformRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'platform',
  beforeLoad: async ({ context }) => {
    let session;
    try {
      session = await context.queryClient.ensureQueryData(sessionQuery);
    } catch {
      throw redirect({ href: '/api/auth/login' });
    }
    if (!session.platform) throw redirect({ to: '/' });
    return { session };
  },
  component: PlatformShell,
});

const platformAgenciesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/',
  component: PlatformAgenciesPage,
});

const platformAgencyDetailRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'agenzie/$id',
  component: PlatformAgencyDetailPage,
});

/*
 * The operator's lists keep their filters in the URL, as the kit lists do: a
 * filtered view survives a reload and can be sent to a colleague. A value the
 * schema does not recognise is dropped rather than failing the page — a stale
 * link should open the list, not an error.
 */
const optional = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

const platformUsersRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'utenti',
  validateSearch: z.object({
    q: optional(z.string()),
    agencyId: optional(z.string().uuid()),
  }),
  component: PlatformUsersPage,
});

const platformWebsiteLeadsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'richieste',
  validateSearch: z.object({
    q: optional(z.string()),
    kind: optional(websiteLeadKindSchema),
    status: optional(websiteLeadStatusSchema),
  }),
  component: PlatformWebsiteLeadsPage,
});

const platformAssistantRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'assistente',
  component: AssistantAdminPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  component: DashboardPage,
});

/**
 * The one screen that is not a resource: the calendar and the reminder
 * worklist are views over activities, not a table anyone browses.
 */
const agendaRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: 'agenda',
  // The reminder worklist is a kit list, so its filters and sort belong in the
  // URL like every other list's (U5) — a filtered view has to survive a reload
  // and be shareable.
  // Typed as a partial: every key is optional in the URL, and saying so is what
  // lets a plain `<Link to="/agenda">` elsewhere stay a plain link.
  validateSearch: (input: Record<string, unknown>): Partial<ListState> =>
    listSearchSchema.parse(input),
  component: AgendaPage,
});

/** Administration (U7). The API refuses these to anyone but an admin; the nav
 *  hides them for the same reason, so nobody is offered a locked door. */
const administrationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: 'utenti',
  component: AdministrationPage,
});

/**
 * Comunicazioni → WhatsApp (M8). One route for both faces of the screen: the
 * connect card and the inbox are two moments of the same thing.
 */
const whatsappRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/comunicazioni/whatsapp',
  component: WhatsAppPage,
});

/**
 * Comunicazioni → Impostazioni (M8.1). Everything about a channel that is not
 * the conversation: the number, what an import brought, what can be removed.
 * Its own route because the inbox is a place somebody sits, and settings bolted
 * to the top of it are furniture in the way.
 */
const communicationsSettingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/comunicazioni/impostazioni',
  component: CommunicationsSettingsPage,
});

/**
 * Comunicazioni → Campagne (M9 / P2). The list and one campaign are two routes
 * rather than one screen with a panel: a campaign carries a sequence, a state
 * and a list of people, which is a page rather than a drawer.
 */
const campaignsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/comunicazioni/campagne',
  component: CampaignsPage,
});

const campaignDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/comunicazioni/campagne/$campaignId',
  component: CampaignDetailPage,
});

/**
 * The data assistant (M15): a chat over the agency's own data, streamed. Its
 * own top-level route rather than under `/comunicazioni` — it talks to the
 * agency's data, not to a customer.
 */
const assistantRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: 'assistente',
  component: AssistantPage,
  // The one screen that wants the window rather than the reading column the
  // rest of the app is laid out in: a sidebar, a scrolling transcript and a
  // composer pinned to the bottom have nothing to gain from being centred in
  // 1400px of it.
  staticData: { fullBleed: true },
});

/**
 * What the assistant costs this agency (M15.3). A separate route rather than a
 * tab inside the chat: it is read by whoever answers for the spend, not by
 * whoever is mid-question, and it wants the app's ordinary reading column
 * rather than the chat's full-bleed layout.
 */
const assistantUsageRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: 'assistente/statistiche',
  component: AssistantUsagePage,
});

const agencyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: 'agenzia',
  component: AgencySettingsPage,
});

/*
 * Below, four routes per resource declaration. Permissions are read per render
 * from the session's serialized abilities, so a role that cannot create simply
 * never sees the button — and the API refuses it anyway.
 */

/**
 * The two tables a campaign is composed out of (M9 / P2). Ordinary resource
 * screens: a saved audience and a message are rows with a form, and the kit
 * already knows how to render those.
 */
const segmentRoutes = createResourceRoutes({
  resource: segmentsResource,
  getParentRoute: () => authenticatedRoute,
  path: 'comunicazioni/segmenti',
  usePermissions: () => useResourcePermissions('Campaign'),
});

const messageTemplateRoutes = createResourceRoutes({
  resource: messageTemplatesResource,
  getParentRoute: () => authenticatedRoute,
  path: 'comunicazioni/template',
  usePermissions: () => useResourcePermissions('Campaign'),
});

const zoneRoutes = createResourceRoutes({
  resource: zonesResource,
  getParentRoute: () => authenticatedRoute,
  path: 'zone',
  usePermissions: () => useResourcePermissions('Zone'),
  // Importing from the national catalogue is a zone-screen affair, so it lives
  // in that list's toolbar rather than in a screen of its own (U6).
  // ODbL asks for attribution wherever the derived data is seen, and the zone
  // list is where it is seen most (U9). It sits beside the import button rather
  // than in a README, and shows whether or not the agency may import.
  useListToolbarExtra: () => {
    const permissions = useResourcePermissions('Zone');
    return (
      <>
        {permissions.create === true && <ZoneImportDialog />}
        <span className="text-secondary text-muted">Zone © OpenStreetMap contributors (ODbL)</span>
      </>
    );
  },
});

const typologyRoutes = createResourceRoutes({
  resource: propertyTypologiesResource,
  getParentRoute: () => authenticatedRoute,
  path: 'tipologie',
  usePermissions: () => useResourcePermissions('PropertyTypology'),
});

const appointmentTypeRoutes = createResourceRoutes({
  resource: appointmentTypesResource,
  getParentRoute: () => authenticatedRoute,
  path: 'tipi-appuntamento',
  usePermissions: () => useResourcePermissions('AppointmentType'),
});

const contactRoutes = createResourceRoutes({
  resource: contactsResource,
  getParentRoute: () => authenticatedRoute,
  path: 'contatti',
  usePermissions: () => useResourcePermissions('Contact'),
});

const propertyRoutes = createResourceRoutes({
  resource: propertiesResource,
  getParentRoute: () => authenticatedRoute,
  path: 'immobili',
  usePermissions: () => useResourcePermissions('Property'),
});

const mandateRoutes = createResourceRoutes({
  resource: mandatesResource,
  getParentRoute: () => authenticatedRoute,
  path: 'incarichi',
  usePermissions: () => useResourcePermissions('Mandate'),
});

const demandRoutes = createResourceRoutes({
  resource: demandsResource,
  getParentRoute: () => authenticatedRoute,
  path: 'richieste',
  usePermissions: () => useResourcePermissions('Demand'),
});

const buildingRoutes = createResourceRoutes({
  resource: buildingsResource,
  getParentRoute: () => authenticatedRoute,
  path: 'edifici',
  usePermissions: () => useResourcePermissions('Building'),
});

const routeTree = rootRoute.addChildren([
  authenticatedRoute.addChildren([
    dashboardRoute,
    agendaRoute,
    administrationRoute,
    assistantRoute,
    assistantUsageRoute,
    whatsappRoute,
    communicationsSettingsRoute,
    campaignsRoute,
    campaignDetailRoute,
    segmentRoutes.listRoute,
    segmentRoutes.newRoute,
    segmentRoutes.detailRoute,
    segmentRoutes.editRoute,
    messageTemplateRoutes.listRoute,
    messageTemplateRoutes.newRoute,
    messageTemplateRoutes.detailRoute,
    messageTemplateRoutes.editRoute,
    agencyRoute,
    zoneRoutes.listRoute,
    zoneRoutes.newRoute,
    zoneRoutes.detailRoute,
    zoneRoutes.editRoute,
    typologyRoutes.listRoute,
    typologyRoutes.newRoute,
    typologyRoutes.detailRoute,
    typologyRoutes.editRoute,
    appointmentTypeRoutes.listRoute,
    appointmentTypeRoutes.newRoute,
    appointmentTypeRoutes.detailRoute,
    appointmentTypeRoutes.editRoute,
    contactRoutes.listRoute,
    contactRoutes.newRoute,
    contactRoutes.detailRoute,
    contactRoutes.editRoute,
    propertyRoutes.listRoute,
    propertyRoutes.newRoute,
    propertyRoutes.detailRoute,
    propertyRoutes.editRoute,
    mandateRoutes.listRoute,
    mandateRoutes.newRoute,
    mandateRoutes.detailRoute,
    mandateRoutes.editRoute,
    demandRoutes.listRoute,
    demandRoutes.newRoute,
    demandRoutes.detailRoute,
    demandRoutes.editRoute,
    buildingRoutes.listRoute,
    buildingRoutes.newRoute,
    buildingRoutes.detailRoute,
    buildingRoutes.editRoute,
  ]),
  platformRoute.addChildren([
    platformAgenciesRoute,
    platformAgencyDetailRoute,
    platformUsersRoute,
    platformWebsiteLeadsRoute,
    platformAssistantRoute,
  ]),
]);

export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }

  /**
   * What a route may tell the shell about itself. Optional, and read in
   * `app-shell.tsx`: declaring it here is what keeps `staticData` typed
   * instead of an untyped bag, so a misspelt key fails the build rather than
   * silently never matching.
   */
  interface StaticDataRouteOption {
    /** Lay this screen out across the window, not in the reading column. */
    fullBleed?: boolean;
  }
}
